"use client";

import { useMemo } from "react";

import { Tab, TabGroup, TabList } from "@headlessui/react";

import type {
  DistrictSchoolLevelType,
  SchoolType,
} from "@/providers/store/api/find-my-school/find-my-school.interface";
import { cn } from "@/shared/helpers/cn.helper";
import { buildFindMySchoolCategoryTabs } from "@/shared/utils/find-my-school-category-tabs.util";

export interface CategoryTabsProps {
  selectedCategory: SchoolType;
  schoolLevelTypes: readonly DistrictSchoolLevelType[];
  mobile?: boolean;
  onChange: (category: SchoolType) => void;
}

export function CategoryTabs({
  selectedCategory,
  schoolLevelTypes,
  mobile = false,
  onChange,
}: CategoryTabsProps) {
  const categories = useMemo(
    () => buildFindMySchoolCategoryTabs(schoolLevelTypes),
    [schoolLevelTypes],
  );
  const selectedIndex = Math.max(0, categories.findIndex((c) => c.key === selectedCategory));

  const handleChange = (index: number) => {
    onChange(categories[index]?.key ?? "all");
  };

  return (
    <div
      className={cn({
        "-mx-3 overflow-x-auto px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden": mobile,
      })}
    >
      <TabGroup
        selectedIndex={selectedIndex}
        onChange={handleChange}
      >
        <TabList
          className={cn("flex gap-2", {
            "w-max": mobile,
            "flex-wrap": !mobile,
          })}
        >
          {categories.map((category) => (
            <Tab
              key={category.key}
              className={({ selected }) =>
                cn(
                  "h-9 whitespace-nowrap rounded-full border px-4 text-[14px]/[20px] font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset",
                  selected
                    ? "border-[#520313] bg-[#520313] text-white focus-visible:ring-white"
                    : "border-[#E5E4E3] bg-white text-[#4B4849] focus-visible:ring-[#520313]",
                )
              }
            >
              {category.label}
            </Tab>
          ))}
        </TabList>
      </TabGroup>
    </div>
  );
}
